import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessao } from '../nucleo/sessao';
import { mensagemDeErro } from '../nucleo/api';
import { useToast } from '../componentes/toast';

/** Textos da faixa: o ambiente do servidor fora da produção e o workspace Sandbox (função pura para teste). */
export function avisosDeAmbiente(ambiente: string | undefined, sandbox: boolean): ('homologacao' | 'staging' | 'sandbox')[] {
  const lista: ('homologacao' | 'staging' | 'sandbox')[] = [];
  if (ambiente === 'homologacao' || ambiente === 'staging') lista.push(ambiente);
  if (sandbox) lista.push('sandbox');
  return lista;
}

/**
 * Fase 2 (W1, D30 com a regra do Gustavo): faixa vermelha fixa de 24px quando o servidor é de homologação ou staging
 * (`APP_AMBIENTE`, que a sessão traz) e TAMBÉM quando o workspace atual é Sandbox, com a mesma cor. Token `--ambiente`
 * (AA com o texto branco nos dois temas). O conteúdo desce por `--faixa-h` (marcado no `<html>`), sem cobrir a barra
 * superior nem o topo da barra lateral. Não sai na impressão.
 *
 * val4 (29/10): na simulação de usuário de teste a mesma faixa diz "Simulando: <nome>" com o botão "Voltar para mim", nas
 * duas cascas e em todas as telas (o nome se corta; o botão nunca).
 */
export function FaixaAmbiente() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const [voltando, setVoltando] = useState(false);
  const avisos = avisosDeAmbiente(s.ambiente, s.workspace.sandbox);
  const simulacao = s.simulacao ?? null;
  const ativa = avisos.length > 0 || !!simulacao;
  const voltar = async () => {
    setVoltando(true);
    try {
      await s.voltarDaSimulacao();
    } catch (e) {
      setVoltando(false);
      toast.erro(mensagemDeErro(e));
    }
  };
  useEffect(() => {
    const raiz = document.documentElement;
    if (ativa) raiz.dataset.faixaAmbiente = '';
    else delete raiz.dataset.faixaAmbiente;
    return () => {
      delete raiz.dataset.faixaAmbiente;
    };
  }, [ativa]);
  if (!ativa) return null;
  return (
    <div role="status" aria-label={t('ambiente.rotulo')} data-faixa-ambiente data-sem-impressao className="faixa-ambiente fixed inset-x-0 top-0 flex items-center justify-center gap-2 overflow-hidden bg-ambiente px-3 text-xs font-semibold text-ambiente-tinta">
      {avisos.map((a, k) => (
        <span key={a} className={simulacao ? 'hidden truncate sm:inline' : 'truncate'} title={t(`ambiente.${a}`)}>
          {k > 0 && <span aria-hidden className="mr-2">·</span>}
          {t(`ambiente.${a}`)}
        </span>
      ))}
      {simulacao && (
        <span data-faixa-simulacao="" role="group" aria-label={t('simulacao.rotulo')} className="flex min-w-0 items-center gap-2">
          {avisos.length > 0 && (
            <span aria-hidden className="hidden sm:inline">
              ·
            </span>
          )}
          <span className="truncate" title={t('simulacao.faixa', { nome: s.usuario.nome })}>
            {t('simulacao.faixa', { nome: s.usuario.nome })}
          </span>
          <span aria-hidden>·</span>
          <button
            type="button"
            data-voltar-simulacao=""
            onClick={() => void voltar()}
            disabled={voltando}
            aria-busy={voltando}
            className="shrink-0 rounded px-1 font-semibold whitespace-nowrap underline underline-offset-2 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-current disabled:opacity-70"
          >
            {voltando ? t('simulacao.voltando') : t('simulacao.voltar')}
          </button>
        </span>
      )}
    </div>
  );
}
